import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Link, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useLogout } from '@/features/logout';
import { cn } from '@/shared/lib/utils/classnames';
import { useAuthStore } from '@/shared/store/auth';
import { FlexBetween } from '@/shared/ui/layout/FlexBetween';
import { AppLogo } from '@/shared/ui/logo';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/shared/ui/sheet';
import { ActionButtons } from './UserMenu';

/**
 * 햄버거 → X 애니메이션 아이콘
 * CSS transform으로 부드러운 전환 구현
 */
function AnimatedMenuIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="overflow-visible"
    >
      <line
        x1="4"
        y1="6"
        x2="20"
        y2="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className={cn(
          'origin-center transition-transform duration-300',
          isOpen && 'translate-y-1.5 rotate-45',
        )}
        style={{ transitionTimingFunction: 'var(--ease-out-expo)' }}
      />
      <line
        x1="4"
        y1="12"
        x2="20"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className={cn(
          'origin-center transition-all duration-200',
          isOpen ? 'opacity-0 scale-x-0' : 'opacity-100 scale-x-100',
        )}
        style={{ transitionTimingFunction: 'var(--ease-out-expo)' }}
      />
      <line
        x1="4"
        y1="18"
        x2="20"
        y2="18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className={cn(
          'origin-center transition-transform duration-300',
          isOpen && '-translate-y-1.5 -rotate-45',
        )}
        style={{ transitionTimingFunction: 'var(--ease-out-expo)' }}
      />
    </svg>
  );
}

/** Drawer 메뉴 아이템 아이콘 */
function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 3L11 8L6 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HelpCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666ZM7.575 7.5a2.5 2.5 0 0 1 4.858.833c0 1.667-2.5 2.5-2.5 2.5M10 14.167h.008"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M16.667 17.5v-1.667a3.333 3.333 0 0 0-3.334-3.333H6.667a3.333 3.333 0 0 0-3.334 3.333V17.5M10 9.167A3.333 3.333 0 1 0 10 2.5a3.333 3.333 0 0 0 0 6.667Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogOutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M7.5 17.5H4.167A1.667 1.667 0 0 1 2.5 15.833V4.167A1.667 1.667 0 0 1 4.167 2.5H7.5M13.333 14.167 17.5 10l-4.167-4.167M17.5 10h-10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LogInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M12.5 17.5h3.333A1.667 1.667 0 0 0 17.5 15.833V4.167A1.667 1.667 0 0 0 15.833 2.5H12.5M8.333 14.167 12.5 10 8.333 5.833M12.5 10h-10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 네비게이션 링크 목록 */
const NAV_LINKS = [
  { to: '/agents', label: '에이전트 리스트' },
  { to: '/market', label: '마켓 플레이스' },
  { to: '/contact', label: '문의하기' },
] as const;

/** Drawer 메뉴 아이템 스타일 */
const menuItemBaseStyles = cn(
  'flex items-center gap-3 py-3 px-6 -mx-6 w-[calc(100%+3rem)]',
  'text-base font-medium text-foreground',
  'hover:bg-muted/50 active:bg-muted transition-colors',
);

/**
 * Drawer 메뉴 아이템 컴포넌트
 *
 * 레이아웃: [아이콘 20px] [라벨 flex-1] [배지/chevron 고정폭]
 */
function DrawerMenuItem({
  to,
  icon,
  label,
  onClick,
  index,
  variant = 'default',
  disabled = false,
  badge,
}: {
  to?: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  index: number;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  badge?: string;
}) {
  const baseStyles = cn(
    menuItemBaseStyles,
    variant === 'destructive' && 'text-destructive hover:bg-destructive/10',
    disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent active:bg-transparent',
  );

  const animationStyle = {
    animationDelay: `${index * 50}ms`,
    animationDuration: '350ms',
    animationFillMode: 'backwards' as const,
    animationTimingFunction: 'var(--ease-out-expo)',
  };

  // 우측 영역: badge 또는 chevron (항상 고정 너비 확보)
  const rightSlot = badge ? (
    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap">
      {badge}
    </span>
  ) : (
    <span className="w-4 flex justify-center opacity-40">
      <ChevronRightIcon />
    </span>
  );

  const content = (
    <>
      {/* 좌측 아이콘: 고정 너비 20px */}
      <span className="w-5 shrink-0 flex justify-center opacity-60">{icon}</span>
      {/* 라벨: flex-1 */}
      <span className="flex-1">{label}</span>
      {/* 우측: badge 또는 chevron */}
      {rightSlot}
    </>
  );

  const itemClass = cn(baseStyles, 'text-left animate-in fade-in slide-in-from-right-4');

  if (to && !disabled) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={itemClass}
        style={animationStyle}
        activeProps={{
          className: cn(itemClass, 'text-[#0084FF]'),
        }}
        activeOptions={{
          includeSearch: false,
        }}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={itemClass}
      style={animationStyle}
    >
      {content}
    </button>
  );
}

/**
 * 섹션 라벨
 */
function DrawerSectionLabel({ children, index }: { children: React.ReactNode; index: number }) {
  return (
    <span
      className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-6 -mx-6 animate-in fade-in"
      style={{
        animationDelay: `${index * 50}ms`,
        animationDuration: '350ms',
        animationFillMode: 'backwards',
        animationTimingFunction: 'var(--ease-out-expo)',
      }}
    >
      {children}
    </span>
  );
}

/**
 * 공통 앱 헤더 컴포넌트
 *
 * 전체 페이지에서 공유되는 헤더입니다.
 * 로그인 페이지('/login')만 별도 레이아웃을 사용합니다.
 *
 * **반응형 동작**:
 * - 데스크탑 (lg+): 전체 네비게이션 표시
 * - 모바일/태블릿: 햄버거 메뉴로 드로어 열기 (모든 메뉴 통합)
 */
/** 인증이 필요한 라우트인지 확인 */
function isProtectedRoute(pathname: string): boolean {
  // /account만 보호됨 (에이전트 목록/상세는 공개)
  return pathname.startsWith('/account');
}

export function AppHeader() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const isProtected = isProtectedRoute(location.pathname);

  // 보호된 페이지(account)에서 로그아웃 → /agents로 이동
  // 공개 페이지에서 로그아웃 → 현재 페이지 유지
  const { handleLogout: logout, isPending: isLoggingOut } = useLogout({
    redirectTo: isProtected ? '/agents' : false,
    onSuccess: () => {
      toast.success('로그아웃 되었습니다');
      setIsOpen(false);
    },
    onError: () => {
      toast.error('로그아웃 중 오류가 발생했습니다');
      setIsOpen(false);
    },
  });

  // 로그인 페이지는 별도 레이아웃 사용 (split-screen 디자인)
  if (location.pathname === '/login') {
    return null;
  }

  // 에이전트 상세 페이지 (/agents/{agentId}/*) - 간소화된 헤더 사용
  const isAgentDetailPage = /^\/agents\/[^/]+/.test(location.pathname);
  if (isAgentDetailPage) {
    return null; //<AgentDetailHeader />;
  }

  // const isHomePage = location.pathname === '/';

  return (
    <header className={cn('z-50 glass-strong h-18  w-screen sticky top-0 bg-white')}>
      <FlexBetween as="nav" className=" h-18 container-content">
        {/* Logo */}
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <AppLogo />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8 xl:gap-12 h-full">
          <nav className="inline-flex items-center justify-center gap-6 xl:gap-12">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="px-1 text-sm text-center text-black hover:opacity-70 transition-opacity"
                activeProps={{
                  className: '!text-[#0084FF]',
                }}
                activeOptions={{
                  includeSearch: false,
                }}
              >
                {label}
              </Link>
            ))}
          </nav>
          <ActionButtons />
        </div>

        {/* Mobile Menu - 햄버거 버튼만 표시 */}
        <div className="flex lg:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="p-2 -mr-2 text-black hover:opacity-70 transition-opacity"
                aria-label="메뉴 열기"
              >
                <AnimatedMenuIcon isOpen={isOpen} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm flex flex-col">
              <VisuallyHidden>
                <SheetTitle>네비게이션 메뉴</SheetTitle>
              </VisuallyHidden>

              {/* Drawer Content */}
              <div className="flex flex-col gap-6 pt-4 flex-1 -mx-6 px-6">
                {/* 네비게이션 섹션 */}
                <div className="flex flex-col gap-1">
                  <DrawerSectionLabel index={0}>메뉴</DrawerSectionLabel>
                  {NAV_LINKS.map(({ to, label }, index) => (
                    <DrawerMenuItem
                      key={to}
                      to={to}
                      icon={<ChevronRightIcon />}
                      label={label}
                      onClick={() => setIsOpen(false)}
                      index={index + 1}
                    />
                  ))}
                </div>

                {/* 구분선 */}
                <div
                  className="h-px bg-border -mx-6 animate-in fade-in"
                  style={{
                    animationDelay: '150ms',
                    animationDuration: '350ms',
                    animationFillMode: 'backwards',
                  }}
                />

                {/* 계정 섹션 */}
                <div className="flex flex-col gap-1">
                  <DrawerSectionLabel index={3}>계정</DrawerSectionLabel>
                  {isAuthenticated ? (
                    <>
                      <DrawerMenuItem
                        to="/account"
                        icon={<UserIcon />}
                        label="계정 관리"
                        onClick={() => setIsOpen(false)}
                        index={4}
                      />
                      <DrawerMenuItem
                        icon={<LogOutIcon />}
                        label={isLoggingOut ? '로그아웃 중...' : '로그아웃'}
                        onClick={logout}
                        index={5}
                        variant="destructive"
                        disabled={isLoggingOut}
                      />
                    </>
                  ) : (
                    <Link
                      to="/login"
                      search={{ redirect: location.pathname }}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        'flex items-center gap-3 py-3 px-6 -mx-6 w-[calc(100%+3rem)]',
                        'text-base font-medium text-foreground',
                        'hover:bg-muted/50 active:bg-muted transition-colors',
                        'text-left animate-in fade-in slide-in-from-right-4',
                      )}
                      style={{
                        animationDelay: '200ms',
                        animationDuration: '350ms',
                        animationFillMode: 'backwards',
                        animationTimingFunction: 'var(--ease-out-expo)',
                      }}
                    >
                      <span className="w-5 shrink-0 flex justify-center opacity-60">
                        <LogInIcon />
                      </span>
                      <span className="flex-1">로그인</span>
                      <span className="w-4 flex justify-center opacity-40">
                        <ChevronRightIcon />
                      </span>
                    </Link>
                  )}
                </div>

                {/* 구분선 */}
                <div
                  className="h-px bg-border -mx-6 animate-in fade-in"
                  style={{
                    animationDelay: '250ms',
                    animationDuration: '350ms',
                    animationFillMode: 'backwards',
                  }}
                />

                {/* 지원 섹션 */}
                <div className="flex flex-col gap-1">
                  <DrawerSectionLabel index={6}>지원</DrawerSectionLabel>
                  <DrawerMenuItem
                    icon={<HelpCircleIcon />}
                    label="도움말"
                    index={7}
                    disabled
                    badge="준비중입니다"
                  />
                </div>
              </div>

              {/* Footer */}
              <div
                className="pt-4 border-t border-border animate-in fade-in"
                style={{
                  animationDelay: '400ms',
                  animationDuration: '350ms',
                  animationFillMode: 'backwards',
                }}
              >
                <p className="text-xs text-muted-foreground text-center">
                  © 2025 AX Studio. All rights reserved.
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </FlexBetween>
    </header>
  );
}
